import type { ReactNode } from 'react';
import { LockOutlined } from '@ant-design/icons';
import { useI18n } from '@/i18n/useI18n';
import { useSession } from '@/session/useSession';
import { useHubTheme } from '@/theme/ThemeContext';
import { Brand } from './Brand';
import { LocaleSwitcher } from './LocaleSwitcher';
import { erasedOr } from '@/lib/erased';

/**
 * The shell around the authenticated views: a sticky header (mark, applicant welcome, case reference,
 * language) and a reassuring privacy footer. The case ref + applicant label come from the session
 * (server-bound); the client never asserts a tenant or entity id.
 *
 * P1: the brand block is <Brand/> (ONE render site, DOM-identical to the previous inline block);
 * a themed hub may override the footer's "powered by" line ({@code footer.privacy} stays
 * i18n-owned — copy is never in the theme, D2). The layout variant rides [data-variant] on
 * <html> (set by the theme provider); CLASSIC is exactly today's layout.
 */
export function RoomShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { session } = useSession();
  const { theme } = useHubTheme();
  const showName = session?.journey?.showApplicantName ?? true;
  const showCaseRef = session?.journey?.showCaseRef ?? true;

  return (
    <div className="dr-shell">
      <header className="dr-header">
        <Brand
          sub={
            session && showName ? t('session.welcome', { name: erasedOr(session.applicantLabel, t('common.erased')) }) : undefined
          }
        />
        <span className="dr-header__spacer" />
        {session && showCaseRef && (
          <span className="dr-caseref" aria-label={t('session.caseLabel')} title={t('session.caseLabel')}>
            {erasedOr(session.caseRef, t('common.erased'))}
          </span>
        )}
        <LocaleSwitcher />
      </header>

      <main className="dr-main">
        {children}

        <div className="dr-footer">
          <LockOutlined className="dr-footer__icon" aria-hidden="true" />
          <div>
            {t('footer.privacy')}
            <br />
            {theme?.brand?.footerLine ?? t('footer.poweredBy')}
          </div>
        </div>
      </main>
    </div>
  );
}
